import { useRef, useState, useEffect, useCallback } from "react";

const AnnotationCanvas = ({
  contentId,
  activeTool,
  color,
  thickness,
  annotations,
  showAnnotations,
  visibleUsers,
  currentUserId,
  onSave,
  containerRef,
}) => {
  const svgRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  const [textInput, setTextInput] = useState(null); // { x, y }
  const [textValue, setTextValue] = useState("");

  const getRelativeCoords = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  // ── Draw tool ──
  const handlePointerDown = useCallback((e) => {
    if (activeTool !== "draw") return;
    e.preventDefault();
    const { x, y } = getRelativeCoords(e);
    setIsDrawing(true);
    setCurrentPath(`M ${x} ${y}`);
  }, [activeTool, getRelativeCoords]);

  const handlePointerMove = useCallback((e) => {
    if (!isDrawing || activeTool !== "draw") return;
    e.preventDefault();
    const { x, y } = getRelativeCoords(e);
    setCurrentPath((prev) => `${prev} L ${x} ${y}`);
  }, [isDrawing, activeTool, getRelativeCoords]);

  const handlePointerUp = useCallback(async (e) => {
    if (!isDrawing || activeTool !== "draw") return;
    e.preventDefault();
    setIsDrawing(false);
    if (currentPath && currentPath.length > 5) {
      await onSave({ type: "draw", path: currentPath, color, thickness });
    }
    setCurrentPath("");
  }, [isDrawing, activeTool, currentPath, color, thickness, onSave]);

  // ── Text tool ──
  const handleSvgClick = useCallback((e) => {
    if (activeTool !== "text") return;
    if (textInput) return; // already placing one
    e.preventDefault();
    const { x, y } = getRelativeCoords(e);
    setTextInput({ x, y });
    setTextValue("");
  }, [activeTool, textInput, getRelativeCoords]);

  const commitText = useCallback(async () => {
    if (textValue.trim() && textInput) {
      await onSave({ type: "text", text: textValue.trim(), x: textInput.x, y: textInput.y, color, thickness });
    }
    setTextInput(null);
    setTextValue("");
  }, [textValue, textInput, color, thickness, onSave]);

  const handleTextKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      commitText();
    }
    if (e.key === "Escape") {
      setTextInput(null);
      setTextValue("");
    }
  }, [commitText]);

  // Focus text input when it appears
  useEffect(() => {
    if (textInput) {
      const el = document.getElementById("annotation-text-input");
      if (el) el.focus();
    }
  }, [textInput]);

  const isVisible = (ann) => {
    if (!showAnnotations) return false;
    if (visibleUsers && visibleUsers.length > 0 && !visibleUsers.includes(ann.user_id)) return false;
    return true;
  };

  const cursor =
    activeTool === "draw" ? "crosshair"
    : activeTool === "text" ? "text"
    : "default";

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
      <svg
        ref={svgRef}
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{
          cursor,
          pointerEvents: activeTool !== "none" ? "all" : "none",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onClick={handleSvgClick}
      >
        {/* Saved annotations */}
        {annotations.map((ann) =>
          isVisible(ann) ? (
            ann.type === "draw" ? (
              <path
                key={ann.id}
                d={ann.path}
                stroke={ann.color}
                strokeWidth={ann.thickness * 0.4}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                opacity={0.85}
              />
            ) : (
              <text
                key={ann.id}
                x={ann.x}
                y={ann.y}
                fill={ann.color}
                fontSize={ann.thickness * 1.2 + 2}
                style={{ userSelect: "none", fontFamily: "sans-serif", fontWeight: 600 }}
                opacity={0.9}
              >
                {ann.text}
              </text>
            )
          ) : null
        )}

        {/* Live drawing path */}
        {currentPath && (
          <path
            d={currentPath}
            stroke={color}
            strokeWidth={thickness * 0.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity={0.85}
          />
        )}
      </svg>

      {/* Text input overlay */}
      {textInput && (
        <div
          className="absolute pointer-events-auto"
          style={{
            left: `${textInput.x}%`,
            top: `${textInput.y}%`,
            transform: "translate(-50%, -50%)",
            zIndex: 20,
          }}
        >
          <input
            id="annotation-text-input"
            type="text"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={handleTextKeyDown}
            onBlur={commitText}
            className="bg-black/60 border border-white/30 rounded px-2 py-1 text-sm text-white outline-none min-w-[120px] backdrop-blur"
            placeholder="Texte… Entrée pour valider"
            style={{ color, fontWeight: 600 }}
          />
        </div>
      )}
    </div>
  );
};

export default AnnotationCanvas;
